import { useMemo } from 'react';

import { Dayjs } from 'dayjs';

import { TimelineBooking } from './timeline.types';
import { barStartDays, fuseAdjacentBars } from './timeline.utils';

export type RowBookingsGetter<R> = (row: R) => TimelineBooking[];

const NO_EDGES: readonly (Dayjs | undefined)[] = [];

/**
 * The row-bar getter every timeline renderer reads through: `getRowBookings` with the bars that
 * meet at a day boundary fused (see fuseAdjacentBars), the start days collected across EVERY row
 * so a day that ends a booking on one row and starts one on another is one line, not a column.
 *
 * `edgeStarts` adds days the rail itself opens a line on (the searched period's or the window's
 * start), so a booking ending the day before meets that line instead of stopping a day short.
 *
 * Both the grid rails (useTimelineColumns) and the list-view strips (the pages that render
 * TimelineListRow) go through this, so the two views agree on where bars meet. Memoised on the
 * rows, the getter and the edge days: consumers keyed on its identity rebuild only when the bars
 * can have changed.
 */
export function useFusedRowBookings<R>(
  rows: R[],
  getRowBookings: RowBookingsGetter<R>,
  edgeStarts?: readonly (Dayjs | undefined)[],
): RowBookingsGetter<R>;
export function useFusedRowBookings<R>(
  rows: R[],
  getRowBookings?: RowBookingsGetter<R>,
  edgeStarts?: readonly (Dayjs | undefined)[],
): RowBookingsGetter<R> | undefined;
export function useFusedRowBookings<R>(
  rows: R[],
  getRowBookings?: RowBookingsGetter<R>,
  edgeStarts: readonly (Dayjs | undefined)[] = NO_EDGES,
): RowBookingsGetter<R> | undefined {
  // A string key, so a caller rebuilding the array each render doesn't break the memo.
  const edgeKey = edgeStarts
    .filter((day): day is Dayjs => !!day?.isValid())
    .map((day) => day.format('YYYY-MM-DD'))
    .join('|');

  return useMemo(() => {
    if (!getRowBookings) return undefined;
    const startDays = barStartDays(rows.flatMap((row) => getRowBookings(row) ?? []));
    if (edgeKey) edgeKey.split('|').forEach((day) => startDays.add(day));
    return (row: R) => fuseAdjacentBars(getRowBookings(row), startDays);
  }, [rows, getRowBookings, edgeKey]);
}

export default useFusedRowBookings;
